
import articleScroll from '@root/modules/article/utils/articleScrollHandler';
const ArticlePaywall = () => import(/* webpackChunkName: "ArticlePaywall" */ '@root/modules/article/components/paywall/ArticlePaywall.vue');
import { TranslateResult } from 'vue-i18n';
import axios from 'axios';

interface TildaWindow extends Window {
  t_onReady: any;
  tn: any;
}

declare const window: TildaWindow;

interface TildaData {
  style: string;
  scripts: string;
  body: string;
  teaser_style: string;
  teaser_scripts: string;
  teaser_body: string;
}

interface Data {
  tildaData: TildaData;
  headScriptsLoaded: boolean;
  intervalId: any;
}

interface Methods {
  initTildaArticle: () => void;
  fetchTildaData: () => void;
  initBodyScripts: () => void;
  loadScriptSynchronously: (src: string) => void;
  copyLink: (string: string) => void;
  showLayer: (text: TranslateResult | string) => void;
}

interface Computed {
  isAdultArticle: boolean;
  clearBody: string;
  hasChannelAccess: boolean;
}

interface Props {
  articleId: number;
  isArticleFetching: boolean;
  paywallEnabled: boolean;
}

export default articleScroll.extend<Data, Methods, Computed, Props>({
  components: {
    ArticlePaywall,
  },
  props: {
    articleId: {
      type: Number,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
    paywallEnabled: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      tildaData: {
        style: '',
        scripts: '',
        body: '',
        teaser_style: '',
        teaser_scripts: '',
        teaser_body: '',
      },
      headScriptsLoaded: false,
      intervalId: null,
    };
  },
  computed: {
    isAdultArticle() {
      return this.article.settings['icon.adult'] || false;
    },
    hasChannelAccess() {
      return this.$store.state.piano.access.channelAccess;
    },
    clearBody() {
      const bodyHtml = this.hasChannelAccess ? this.tildaData.body : this.tildaData.teaser_body;
      const clearHtml = bodyHtml.replace(/<script(?![^>]*src=)[^>]*>([\s\S]*?)<\/script>/gi, '');
      return clearHtml;
    },
  },
  async mounted() {
    await this.fetchTildaData();

    this.$nextTick(() => {
      this.$emit('article-loaded', this.articleId);
    });
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    async fetchTildaData() {
      const url = 'https://www.delfi.lt/apps/tilda-api';
      const data = {
        articleId: this.articleId,
      };

      axios
        .post(url, data)
        .then((response) => {
          this.tildaData = response.data;
          return true;
        })
        .catch((error) => {
          console.error('Error:', error);
          return false;
        });
    },

    initBodyScripts() {
      const tempDiv = document.createElement('div');
      // deepcode ignore DOMXSS: <data is not user input>
      tempDiv.innerHTML = this.hasChannelAccess ? this.tildaData.body : this.tildaData.teaser_body;

      const bodyScriptElements = tempDiv.querySelectorAll('script');

      bodyScriptElements.forEach((scriptElement) => {
        if (scriptElement.textContent) {
          // deepcode ignore CodeInjection: <script source is controlled and from known origin>
          eval(scriptElement.textContent);
        }
      });
    },
    loadScriptSynchronously(src) {
      return new Promise((resolve, reject) =>
        this.$nextTick(() => {
          const script = document.createElement('script');
          // deepcode ignore DOMXSS: <data is not user input>
          script.src = src;
          script.async = false; // Ensure the script is executed in order
          script.onload = () => resolve(src);
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
          document.head.appendChild(script);
        })
      );
    },

    async initTildaArticle() {
      const scripts = this.hasChannelAccess ? this.tildaData.scripts : this.tildaData.teaser_scripts;
      const scriptElements = [...scripts.matchAll(/src="([^"]+)"/g)].map((match) => match[1]);

      scriptElements.forEach((scriptElement) => {
        this.loadScriptSynchronously(scriptElement);
        this.headScriptsLoaded = true;
      });

      const tempDivStyles = document.createElement('div');
      // deepcode ignore DOMXSS: <data is not user input>
      tempDivStyles.innerHTML = this.hasChannelAccess ? this.tildaData.style : this.tildaData.teaser_style;

      const styleElements = tempDivStyles.querySelectorAll('link[rel="stylesheet"]');

      styleElements.forEach((styleElement) => {
        document.head.appendChild(styleElement);
      });

      await this.$nextTick();

      this.intervalId = setInterval(() => {
        if (typeof window.t_onReady === 'function') {
          this.initBodyScripts();
          clearInterval(this.intervalId);
        }
      }, 100);

      // add copy url event listener
      const copyUrlButton = document.querySelector<HTMLElement>('.copy-url');

      if (copyUrlButton) {
        copyUrlButton.style.cursor = 'pointer';
        copyUrlButton.addEventListener('click', () => {
          this.copyLink(window.location.href);
        });
      }
    },

    copyLink(string) {
      let textarea;
      let result;
      try {
        textarea = document.createElement('textarea');
        textarea.setAttribute('readonly', 'true');
        textarea.setAttribute('contenteditable', 'true');
        textarea.style.position = 'fixed'; // prevent scroll from jumping to the bottom when focus is set.
        textarea.value = string;

        // deepcode ignore DOMXSS: <data is not user input>
        document.body.appendChild(textarea);

        textarea.select();

        const range = document.createRange();
        range.selectNodeContents(textarea);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        textarea.setSelectionRange(0, textarea.value.length);
        result = document.execCommand('copy');
        this.showLayer(this.$t(`info_layer.url_copied`));
      } catch (err) {
        result = null;
      } finally {
        if (textarea) {
          document.body.removeChild(textarea);
        }
      }

      // manual copy fallback using prompt
      if (!result) {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        const copyHotkey = isMac ? '⌘C' : 'CTRL+C';
        result = prompt(`Press ${copyHotkey}`, string);
        if (!result) {
          return false;
        }
      }
      return true;
    },
    showLayer(text) {
      if (!text) {
        return false;
      }
      const $infoLayer = document.createElement('div');
      $infoLayer.innerHTML = '<span>' + text + '</span>';
      $infoLayer.classList.add('info-layer');
      $infoLayer.style.display = 'block';
      document.body.appendChild($infoLayer);
      setTimeout(function () {
        $infoLayer.style.display = 'none';
        $infoLayer.parentNode?.removeChild($infoLayer);
      }, 2000);
    },
  },
  watch: {
    clearBody: {
      handler(value) {
        if (value && value !== '') {
          this.initTildaArticle();
        }
      },
    },
  },
});
